import { useContext, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import SuiBox from "components/SuiBox";
import SuiButton from "components/SuiButton";
import SuiInput from "components/SuiInput";
import SuiTypography from "components/SuiTypography";
import SuiBadgeDot from "components/SuiBadgeDot";

import ImageCardWrapper from "components/UpStyled/ImageCardWrapper";

import DataTable from "examples/Tables/DataTable";
import Actionables from "components/UpStyled/Actionables";

import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";

import Tooltip from "@mui/material/Tooltip";

import DownloadIcon from "@mui/icons-material/Download";
import DeleteForeverRoundedIcon from "@mui/icons-material/DeleteForeverRounded";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import ReplayIcon from "@mui/icons-material/Replay";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import EditIcon from "@mui/icons-material/Edit";

import Prompt from "components/UpStyled/Prompt";
import toast from "react-hot-toast";
import moment from "moment";

import api from "services/api";
import { nanoid } from "nanoid";
import JSZip from "jszip";
import FileSaver from "file-saver";
import { saveAs } from "file-saver";

import { socket } from "services/request";

const ProjectTable = () => {
  const [projectList, setProjectList] = useState([]);

  const [importVisible, setImportVisible] = useState(false);
  const [selectedProject, setSelectedProject] = useState({});
  const [deleteVisible, setDeleteVisible] = useState(false);

  const [startJobVisible, setStartJobVisible] = useState(false);
  const [reRunJobVisible, setReRunJobVisible] = useState(false);
  const [reRunOption, setRerunOption] = useState("failed");

  const navigate = useNavigate();

  useEffect(() => {
    retrieveProjectList();
  }, []);

  useEffect(() => {
    socket.on(`unionwear-task:update`, () => {
      retrieveProjectList();

      console.log("trigger task update");
    });

    socket.on(`unionwear-task:update-status`, ({ status }) => {
      retrieveProjectList();
    });
  }, []);

  const retrieveProjectList = async () => {
    try {
      const { data } = await api.uwProject.list({ order: "-updated_at" });
      console.log(data);
      setProjectList(data?.uProjects);
    } catch (err) {
      console.log(err);
      toast.error("Failed to retrieve project list!");
    }
  };

  const getProjectTableRow = (item) => {
    let name = item?.name;

    let productNum = item?.products?.data?.length || 0;
    let resulttNum = item?.products?.ai_result?.length || 0;
    let jobNum = 0;
    for (let i = 0; i < item?.products?.data?.length; i++) {
      let sketchNum = item?.products?.data[i].config?.length || 0;
      jobNum += sketchNum;
    }

    return {
      key: item.id,
      name: name,
      product_number: productNum,
      created_at: item.created_at,
      job_started_at: item?.started_at,
      job_finished_at: item?.finished_at,
      job_status: item?.status,
      progress: `${resulttNum}/${jobNum}`,
      action: item,
    };
  };

  const formatProjectStatus = (status) => {
    if (status) {
      if (status == "running") {
        return <SuiBadgeDot color="info" badgeContent={"Processing"} />;
      } else if (status == "completed") {
        return <SuiBadgeDot size="xs" color="success" badgeContent={"Completed"} />;
      } else if (status == "failed") {
        return <SuiBadgeDot color="error" badgeContent={"Failed"} />;
      } else {
        return <SuiBadgeDot color="secondary" badgeContent={status} />;
      }
    } else {
      return null;
    }
  };

  const handleDeleteProject = async (project) => {
    try {
      await api.uwProject.delete(project.id);
      setDeleteVisible(false);
      retrieveProjectList();
    } catch (err) {
      console.log(err);
      toast.error("Failed to delete the project!");
    }
  };

  const startAiJob = async (project) => {
    try {
      let products = project?.products?.data || {};
      let productIds = products.map((x) => x.id);

      let newMeta = {
        selected_images: {},
        task_status: productIds,
      };
      await updateProjectMetaData(project.id, newMeta);
      const { data } = api.uwProject.projectAiTasks({ id: project.id, product_ids: productIds });
      setStartJobVisible(false);
      retrieveProjectList();
      toast.success("Successfully start the job!");
    } catch (err) {
      toast.error("Failed to start the job!");
      setStartJobVisible(false);
    }
  };

  const reRunAiJob = async (project) => {
    try {
      let meta = project?.metadata || {};
      let products = project?.products?.data || {};
      let productIds = [];

      let newMeta = {
        selected_images: {},
        task_status: [],
      };

      if (reRunOption == "all") {
        productIds = products.map((x) => x.id);
        newMeta = {
          selected_images: meta?.selected_images || {},
          task_status: productIds,
        };
      } else {
        let ai_tasks = project?.products?.ai_result || [];
        let finished_product_ids = ai_tasks
          .filter((x) => x.images && x.images.length > 0)
          .map((x) => x.product_id);

        let failed_ProductIds = products
          .filter((x) => !finished_product_ids.includes(x.id))
          .map((x) => x.id);
        if (failed_ProductIds.length == 0) {
          setReRunJobVisible(false);
          return toast("No failed or uncompleted jobs!");
        }

        productIds = failed_ProductIds;
        newMeta = {
          selected_images: meta?.selected_images || {},
          task_status: productIds,
        };
      }

      if (productIds.length == 0) {
        return toast.success("Task already completed!");
      } else {
        await updateProjectMetaData(project.id, newMeta);
      }

      const { data } = api.uwProject.projectAiTasks({ id: project.id, product_ids: productIds });

      setReRunJobVisible(false);
      retrieveProjectList();
      toast.success("Successfully rerun the job!");
    } catch (err) {
      console.log(err);
      toast.error("Failed to rerun the job!");
      setReRunJobVisible(false);
    }
  };

  const updateProjectMetaData = async (project_id, payload) => {
    try {
      const { data } = await api.uwProject.update(project_id, { metadata: payload });
    } catch (err) {
      toast.error("Failed to update task information!");
    }
  };

  const handleDownloadUrls = (items, taskName) => {
    const zip = new JSZip();
    const promises = []; //用于存储多个promise

    if (items.length == 1) {
      let fileName = items[0].code + ".png";
      if (items[0].viewpoint) {
        fileName = items[0].code + "_" + items[0].viewpoint.toUpperCase() + ".png";
      }

      saveAs(items[0].src, fileName);
      return;
    }

    let image_count = {};

    items.forEach((item) => {
      const promise = getFile(item.src).then((res) => {
        let fileName = item.code;
        if (item.viewpoint) {
          fileName = item.code + "_" + item.viewpoint.toUpperCase();
        }

        if (image_count[fileName]) {
          image_count[fileName] = image_count[fileName] + 1;
        } else {
          image_count[fileName] = 1;
        }
        if (image_count[fileName] > 1) {
          fileName = fileName + "_" + (image_count[fileName] - 1);
        }

        zip.file(fileName + ".png", res, { binary: true });
      });
      promises.push(promise);
    });

    if (promises && promises.length > 0) {
      const toastId = toast.loading("Start downing result images...");
      Promise.all(promises)
        .then(() => {
          zip
            .generateAsync({
              type: "blob",
              compression: "DEFLATE", // STORE：默认不压缩 DEFLATE：需要压缩
              compressionOptions: {
                level: 4, // 压缩等级1~9    1压缩速度最快，9最优压缩方式
              },
            })
            .then((res) => {
              toast.success("Download project files started successfully!");
              FileSaver.saveAs(res, taskName || "project.zip");
            })
            .catch((err) => {
              toast.dismiss(toastId);
            });

          toast.dismiss(toastId);
        })
        .catch((err) => console.log("download failed!"));
    } else {
      toast.error("No available result files!");
    }
  };

  const getFile = (url) => {
    return new Promise((resolve, reject) => {
      fetch(url, {
        method: "GET",
      })
        .then((response) => response.blob())
        .then((res) => {
          resolve(res);
        })
        .catch((error) => {
          reject(error);
        });
    });
  };

  const handleDownloadTask = async (projectData) => {
    let resultData = projectData?.products?.ai_result || [];

    let images = [];

    for (let i = 0; i < resultData.length; i++) {
      let rd_images = resultData[i]?.images || [];
      for (let j = 0; j < rd_images.length; j++) {
        images.push({
          src: rd_images[j],
          code: resultData[i]?.task_meta?.code,
          product_id: resultData[i]?.task_meta?.product_id,
          viewpoint: resultData[i]?.task_meta?.viewpoint,
        });
      }
    }
    handleDownloadUrls(images, projectData.name);
  };

  const columns = [
    {
      Header: "Name",
      accessor: "name",
    },
    {
      Header: "Product Number",
      accessor: "product_number",
    },
    {
      Header: "Created At",
      accessor: "created_at",
      Cell: ({ row, value }) => {
        return (
          <div>
            <Tooltip title={moment(value).format("yyyy-MM-DD HH:mm:ss")}>
              <SuiTypography>{moment(value).format("yyyy-MM-DD HH:mm:ss")}</SuiTypography>
            </Tooltip>
          </div>
        );
      },
    },
    {
      Header: "Started At",
      accessor: "job_started_at",
      Cell: ({ row, value }) => {
        return (
          <div>
            {value ? (
              <Tooltip title={moment(value).format("yyyy-MM-DD HH:mm:ss")}>
                <SuiTypography>{moment(value).format("yyyy-MM-DD HH:mm:ss")}</SuiTypography>
              </Tooltip>
            ) : (
              "-"
            )}
          </div>
        );
      },
    },
    {
      Header: "Finished At",
      accessor: "job_finished_at",
      Cell: ({ row, value }) => {
        return (
          <div>
            {value ? (
              <Tooltip title={moment(value).format("yyyy-MM-DD HH:mm:ss")}>
                <SuiTypography>{moment(value).format("yyyy-MM-DD HH:mm:ss")}</SuiTypography>
              </Tooltip>
            ) : (
              "-"
            )}
          </div>
        );
      },
    },
    {
      Header: "Progress",
      accessor: "progress",
    },

    {
      Header: "Status",
      accessor: "job_status",
      Cell: ({ row, value }) => {
        return formatProjectStatus(value);
      },
    },
    {
      Header: "Action",
      accessor: "action",
      isSorted: false,
      canSort: false,
      Cell: ({ row, value }) => {
        return (
          <SuiBox display="flex" alignItems="center" justifyContent="center" gap={0.5}>
            <Tooltip title="View Details">
              <SuiBox>
                <ManageSearchIcon
                  fontSize={"small"}
                  color="dark"
                  sx={{ cursor: "pointer" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate("/unionwear/tasks/" + row.original.key);
                  }}
                />
              </SuiBox>
            </Tooltip>
            |{" "}
            <Tooltip title="Edit">
              <SuiBox>
                <EditIcon
                  fontSize={"small"}
                  color="dark"
                  sx={{ cursor: "pointer" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate("/unionwear/tasks/edit/" + row.original.key);
                  }}
                />
              </SuiBox>
            </Tooltip>
            |{" "}
            {!value?.started_at ? (
              <Tooltip title="Start the task">
                <SuiBox>
                  <PlayArrowIcon
                    fontSize={"small"}
                    color="dark"
                    sx={{ cursor: "pointer" }}
                    onClick={async (e) => {
                      e.stopPropagation();
                      setSelectedProject(value);
                      setStartJobVisible(true);
                      console.log("start the task");
                    }}
                  />{" "}
                </SuiBox>
              </Tooltip>
            ) : (
              <Tooltip title="Rerun">
                <SuiBox>
                  <ReplayIcon
                    fontSize={"small"}
                    color="dark"
                    sx={{ cursor: value?.status != "running" ? "pointer" : "default" }}
                    onClick={async (e) => {
                      e.stopPropagation();
                      if (value?.status != "running") {
                        setSelectedProject(value);
                        setReRunJobVisible(true);
                        console.log("rerun the task");
                      }
                    }}
                  />{" "}
                </SuiBox>
              </Tooltip>
            )}
            |
            <Tooltip title="Download all results">
              <SuiBox>
                <a
                  href={"#"}
                  download
                  onClick={async (e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    handleDownloadTask(value);
                  }}
                >
                  <DownloadIcon fontSize={"small"} color="dark" />{" "}
                </a>
              </SuiBox>
            </Tooltip>
            |
            <Tooltip title="Delete">
              <SuiBox>
                <DeleteForeverRoundedIcon
                  fontSize={"small"}
                  color="error"
                  sx={{ cursor: "pointer" }}
                  onClick={async (e) => {
                    e.stopPropagation();
                    setSelectedProject(value);
                    setDeleteVisible(true);
                  }}
                />{" "}
              </SuiBox>
            </Tooltip>
          </SuiBox>
        );
      },
    },
  ];

  const rows = projectList.map((item) => getProjectTableRow(item));

  return (
    <SuiBox>
      <SuiBox px={3} pt={2}>
        <SuiBox display="flex" justifyContent="space-between" gap={1}>
          <SuiTypography variant="h5">Tasks</SuiTypography>
          <SuiButton
            variant="contained"
            color="info"
            size="small"
            component={Link}
            to="/unionwear/tasks/import-product"
          >
            + New
          </SuiButton>
        </SuiBox>
      </SuiBox>
      <SuiBox px={2}>
        <DataTable
          table={{ columns, rows }}
          entriesPerPage={{
            defaultValue: 20,
            entries: [10, 20, 50, 100],
          }}
          canSearch
          handleRowClick={(row) => navigate("/unionwear/tasks/" + row.original.key)}
        />
      </SuiBox>

      {deleteVisible && selectedProject ? (
        <Prompt
          heading="Delete Project"
          text={`Are you sure you want to delete: ${selectedProject.name}`}
          visible={deleteVisible}
          okText="Delete"
          okColor="error"
          handleOk={() => handleDeleteProject(selectedProject)}
          handleClose={() => setDeleteVisible(false)}
        />
      ) : null}

      {startJobVisible && selectedProject ? (
        <Prompt
          heading="Start Project"
          text={`Are you sure you want to start: ${selectedProject.name}?`}
          visible={startJobVisible}
          okText="Start"
          okColor="info"
          handleOk={() => startAiJob(selectedProject)}
          handleClose={() => setStartJobVisible(false)}
        />
      ) : null}
      {reRunJobVisible && selectedProject ? (
        <Prompt
          heading="Rerun Project"
          text={
            <SuiBox px={2}>
              <SuiTypography as="h4">
                Are you sure you want to rerun the task: {selectedProject.name}?
              </SuiTypography>

              <FormControl sx={{ px: 2 }}>
                <RadioGroup
                  aria-labelledby="demo-radio-buttons-group-label"
                  defaultValue="failed"
                  name="radio-buttons-group"
                  value={reRunOption}
                  onChange={(e) => (e?.target?.value ? setRerunOption(e.target.value) : null)}
                >
                  <FormControlLabel
                    value="failed"
                    control={<Radio />}
                    label={<SuiTypography>Retry failed/uncompleted items. </SuiTypography>}
                  />
                  <FormControlLabel
                    value="all"
                    control={<Radio />}
                    label={<SuiTypography>Rerun all items.</SuiTypography>}
                  />
                </RadioGroup>
              </FormControl>
              <SuiTypography>
                All previous results will be <strong>preserved</strong>
              </SuiTypography>
            </SuiBox>
          }
          visible={reRunJobVisible}
          okText="Rerun"
          okColor="info"
          handleOk={() => reRunAiJob(selectedProject)}
          handleClose={() => setReRunJobVisible(false)}
        />
      ) : null}
    </SuiBox>
  );
};

export default ProjectTable;
